import { call, put } from 'redux-saga/effects';
import {
    requestGetProjectAiSummary,
    requestProjectAiSummary,
    requestGetDailyActivityAiSummary,
    requestDailyActivityAiSummary,
    requestGetWeeklyActivityAiSummary,
    requestWeeklyActivityAiSummary,
    requestGetMonthlyActivityAiSummary,
    requestMonthlyActivityAiSummary,
    requestGetEndOfDayChecklist,
    requestRegenerateQuote,
    requestGetSuggestions,
    requestGetSuggestion,
    requestApproveSuggestion,
    requestRejectSuggestion,
    requestGetSuggestionRecipients,
    requestRedirectSuggestion,
    requestGenerateSuggestions,
    requestAskAiQuestion,
    requestExportAiConversation,
    requestGetAiAnswer,
    requestGetAiConversation,
} from '../requests/ai';
import { setCurrentQuote } from '../../slices/quote';
import { setPendingSuggestionCount } from '../../slices/suggestion';
import { showAlert } from '../../slices/alerts';
import { safeErrorMessage } from '../../../utils/errorUtils';

export function* handleGetProjectAiSummary(action) {
    try {
        const { projectId, callback } = action.payload;
        const response = yield call(requestGetProjectAiSummary, projectId);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const message = safeErrorMessage(error);
        if (action.payload?.callback) action.payload.callback({ ok: false, message });
    }
}

export function* handleRequestProjectAiSummary(action) {
    try {
        const { projectId, callback } = action.payload;
        const response = yield call(requestProjectAiSummary, projectId);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
        if (action.payload?.callback) action.payload.callback({ ok: false, message });
    }
}

export function* handleGetDailyActivityAiSummary(action) {
    try {
        const { date, callback } = action.payload;
        const response = yield call(requestGetDailyActivityAiSummary, date);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const message = safeErrorMessage(error);
        if (action.payload?.callback) action.payload.callback({ ok: false, message });
    }
}

export function* handleRequestDailyActivityAiSummary(action) {
    try {
        const { date, callback } = action.payload;
        const response = yield call(requestDailyActivityAiSummary, date);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
        if (action.payload?.callback) action.payload.callback({ ok: false, message });
    }
}

export function* handleGetWeeklyActivityAiSummary(action) {
    try {
        const { weekStart, callback } = action.payload;
        const response = yield call(requestGetWeeklyActivityAiSummary, weekStart);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const message = safeErrorMessage(error);
        if (action.payload?.callback) action.payload.callback({ ok: false, message });
    }
}

export function* handleRequestWeeklyActivityAiSummary(action) {
    try {
        const { weekStart, callback } = action.payload;
        const response = yield call(requestWeeklyActivityAiSummary, weekStart);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
        if (action.payload?.callback) action.payload.callback({ ok: false, message });
    }
}

export function* handleGetMonthlyActivityAiSummary(action) {
    try {
        const { month, callback } = action.payload;
        const response = yield call(requestGetMonthlyActivityAiSummary, month);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const message = safeErrorMessage(error);
        if (action.payload?.callback) action.payload.callback({ ok: false, message });
    }
}

export function* handleRequestMonthlyActivityAiSummary(action) {
    try {
        const { month, callback } = action.payload;
        const response = yield call(requestMonthlyActivityAiSummary, month);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
        if (action.payload?.callback) action.payload.callback({ ok: false, message });
    }
}

export function* handleGetEndOfDayChecklist(action) {
    try {
        const { date, callback } = action.payload;
        const response = yield call(requestGetEndOfDayChecklist, date);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const message = safeErrorMessage(error);
        const { callback } = action.payload || {};
        if (callback) callback({ ok: false, message });
    }
}

export function* handleRegenerateQuote(action) {
    const { callback } = action.payload;
    try {
        const { quoteId, options } = action.payload;
        const { managerInput = '' } = options || {};
        const body = { managerInput };
        // The API reads an absent key as "keep the current template" and an explicit null as "clear it".
        if (options && Object.prototype.hasOwnProperty.call(options, 'quoteTemplateId')) {
            body.quoteTemplateId = options.quoteTemplateId || null;
        }
        const response = yield call(requestRegenerateQuote, quoteId, body);
        if (callback) callback(response.data);
        else yield put(setCurrentQuote(response.data));
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

// The page renders its own inline error, so a failure here must not also raise an alert.
export function* handleGetSuggestions(action) {
    const { params, callback } = action.payload || {};
    let outcome;
    try {
        const response = yield call(requestGetSuggestions, params || {});
        outcome = { ok: true, items: response.data?.items || [], total: response.data?.total || 0 };
    } catch (error) {
        outcome = { ok: false, message: safeErrorMessage(error) };
    }
    if (callback) callback(outcome);
}

export function* handleGetSuggestionDetail(action) {
    const { suggestionId, callback } = action.payload || {};
    let outcome;
    try {
        const response = yield call(requestGetSuggestion, suggestionId);
        outcome = { ok: true, suggestionId, suggestion: response.data?.suggestion, validity: response.data?.validity };
    } catch (error) {
        outcome = { ok: false, suggestionId, message: safeErrorMessage(error) };
    }
    if (callback) callback(outcome);
}

// A failure must never raise an alert or block the drawer.
export function* handleGetPendingSuggestionCount(action) {
    try {
        const { scope } = action.payload || {};
        const response = yield call(requestGetSuggestions, { scope, status: 'pending', limit: 1 });
        yield put(setPendingSuggestionCount(response.data?.total || 0));
    } catch (error) {
        yield put(setPendingSuggestionCount(0));
    }
}

export function* handleApproveSuggestion(action) {
    const { suggestionId, reason, callback } = action.payload || {};
    let outcome;
    try {
        const response = yield call(requestApproveSuggestion, suggestionId, { reason });
        outcome = { ok: true, suggestion: response.data?.suggestion };
    } catch (error) {
        const status = error?.response?.status;
        const data = error?.response?.data || {};
        // A 500 from /approve still carries the row it left behind in `failed`.
        const suggestion = data.suggestion;
        if (status === 409) {
            outcome = { ok: false, conflict: true, suggestion, message: data.reason || data.msg || 'This suggestion can no longer be approved.' };
        } else {
            const message = data.error || safeErrorMessage(error);
            yield put(showAlert({ severity: 'error', message }));
            outcome = { ok: false, suggestion, message };
        }
    }
    if (callback) callback(outcome);
}

export function* handleRejectSuggestion(action) {
    const { suggestionId, reason, callback } = action.payload || {};
    let outcome;
    try {
        const response = yield call(requestRejectSuggestion, suggestionId, { reason });
        outcome = { ok: true, suggestion: response.data?.suggestion };
    } catch (error) {
        const status = error?.response?.status;
        const data = error?.response?.data || {};
        if (status === 409) {
            outcome = { ok: false, conflict: true, message: data.reason || data.msg || 'This suggestion can no longer be rejected.' };
        } else {
            const message = safeErrorMessage(error);
            yield put(showAlert({ severity: 'error', message }));
            outcome = { ok: false, message };
        }
    }
    if (callback) callback(outcome);
}

export function* handleGetSuggestionRecipients(action) {
    const { suggestionId, callback } = action.payload || {};
    let outcome;
    try {
        const response = yield call(requestGetSuggestionRecipients, suggestionId);
        outcome = { ok: true, users: response.data?.users || [] };
    } catch (error) {
        outcome = { ok: false, users: [], message: safeErrorMessage(error) };
    }
    if (callback) callback(outcome);
}

export function* handleRedirectSuggestion(action) {
    const { suggestionId, userId, callback } = action.payload || {};
    let outcome;
    try {
        const response = yield call(requestRedirectSuggestion, suggestionId, { userId });
        outcome = { ok: true, suggestion: response.data?.suggestion };
    } catch (error) {
        const status = error?.response?.status;
        const data = error?.response?.data || {};
        if (status === 409) {
            outcome = { ok: false, conflict: true, message: data.msg || 'This suggestion can no longer be sent to someone else.' };
        } else {
            const message = data.msg || safeErrorMessage(error);
            yield put(showAlert({ severity: 'error', message }));
            outcome = { ok: false, message };
        }
    }
    if (callback) callback(outcome);
}

export function* handleGenerateSuggestions(action) {
    const { callback } = action.payload || {};
    let outcome;
    try {
        const response = yield call(requestGenerateSuggestions);
        outcome = { ok: true, data: response.data };
    } catch (error) {
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
        outcome = { ok: false, message };
    }
    if (callback) callback(outcome);
}

// The chat window renders its own failure bubble, so these must never raise an alert.
export function* handleAskAiQuestion(action) {
    const { question, conversationId, callback } = action.payload || {};
    let outcome;
    try {
        const response = yield call(requestAskAiQuestion, { question, conversationId: conversationId || undefined });
        outcome = { ok: true, data: response.data };
    } catch (error) {
        outcome = { ok: false, message: safeErrorMessage(error) };
    }
    if (callback) callback(outcome);
}

export function* handleExportAiConversation(action) {
    const { conversationId, format, title, callback } = action.payload || {};
    let outcome;
    try {
        const response = yield call(requestExportAiConversation, conversationId, { format, title: title || undefined });
        outcome = { ok: true, file: response.data?.file };
    } catch (error) {
        outcome = { ok: false, message: safeErrorMessage(error) };
    }
    if (callback) callback(outcome);
}

export function* handleGetAiAnswer(action) {
    const { contextId, callback } = action.payload || {};
    let outcome;
    try {
        const response = yield call(requestGetAiAnswer, contextId);
        outcome = { ok: true, contextId, record: response.data?.record || null };
    } catch (error) {
        outcome = { ok: false, contextId, notFound: error?.response?.status === 404, message: safeErrorMessage(error) };
    }
    if (callback) callback(outcome);
}

export function* handleGetAiConversation(action) {
    const { conversationId, callback } = action.payload || {};
    let outcome;
    try {
        const response = yield call(requestGetAiConversation, conversationId);
        outcome = { ok: true, conversationId, turns: response.data?.turns || [] };
    } catch (error) {
        outcome = { ok: false, conversationId, notFound: error?.response?.status === 404, message: safeErrorMessage(error) };
    }
    if (callback) callback(outcome);
}
